// Copyright (c) Bentley Systems

/**
 * Machine-readable malware scan outcomes the API reports when a document download is
 * blocked with HTTP 403. These mirror the backend `FileMalwareCode` enum names and are
 * carried in the RFC 7807 problem+json body as the `code` extension, e.g.
 * `{ "status": 403, "title": "Forbidden", "code": "MALWARE_FOUND" }`.
 */
export type MalwareScanCode = "NOT_SCANNED" | "MALWARE_FOUND" | "UNKNOWN";

const MALWARE_SCAN_CODES: readonly MalwareScanCode[] = [
  "NOT_SCANNED",
  "MALWARE_FOUND",
  "UNKNOWN",
];

/**
 * Extracts the malware scan code from a blocked (403) document download response.
 *
 * Returns the recognized {@link MalwareScanCode} when the response body is problem+json
 * carrying a known `code`. Returns `undefined` for any other 403 (e.g. a plain-text
 * "Forbidden." access denial) or a body that is not valid JSON, so callers can fall back
 * to generic error handling.
 */
export const parseMalwareScanCode = async (
  response: Response,
): Promise<MalwareScanCode | undefined> => {
  try {
    const body = await response.json();
    const code = body?.code;
    if (MALWARE_SCAN_CODES.includes(code)) {
      return code as MalwareScanCode;
    }
  } catch {
    // Body was not JSON (e.g. plain text "Forbidden.") - no malware code present.
  }
  return undefined;
};
